/**
 * Payments & Dues data access.
 *
 * Open list: BusinessOwner/Accountant see every unpaid/partial sale;
 * Salespeople query salespersonId == self (required by Firestore rules).
 * Order-linked sales use the Ordease order id as the document id so the
 * same order cannot be punched twice.
 *
 * List queries try indexed filters first, then fall back to a simple
 * read + client filter so missing composite indexes do not blank the page.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where
} from 'firebase/firestore';
import { db, auth } from '../../../firebaseConfig';
import { useOrganization } from '../../../contexts/OrganizationContext';
import {
  ELIGIBLE_ORDER_STATUSES,
  OPEN_STATUSES,
  RECEIVABLE_SOURCE,
  RECEIVABLE_STATUS,
  computeStatus,
  emptyReminders,
  roundMoney,
  startOfDay
} from '../receivablesUtils';

export const RECEIVABLE_ERRORS = {
  ORDER_ALREADY_LINKED: 'ORDER_ALREADY_LINKED',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  ORDER_NOT_ELIGIBLE: 'ORDER_NOT_ELIGIBLE',
  AMOUNT_EXCEEDS_OUTSTANDING: 'AMOUNT_EXCEEDS_OUTSTANDING',
  AMOUNT_BELOW_RECEIVED: 'AMOUNT_BELOW_RECEIVED',
  PERMISSION_DENIED: 'PERMISSION_DENIED'
};

const toTimestamp = (value) => {
  const date = startOfDay(value);
  return date ? Timestamp.fromDate(date) : null;
};

const mapDoc = (item) => ({ id: item.id, ...item.data() });

const isIndexError = (error) => error?.code === 'failed-precondition';

const isPermissionError = (error) => error?.code === 'permission-denied';

const wrapFirestoreError = (error) => {
  if (isPermissionError(error)) {
    return new Error(RECEIVABLE_ERRORS.PERMISSION_DENIED);
  }
  return error;
};

const normalizeSearchTerm = (rawNumber) => String(rawNumber || '').replace(/^#/, '').trim();

const stripUndefined = (payload) => {
  const cleaned = {};
  Object.entries(payload).forEach(([key, value]) => {
    cleaned[key] = value === undefined ? null : value;
  });
  return cleaned;
};

export const useReceivables = (listMode = 'open') => {
  const { organizationId, userRole } = useOrganization();
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const receivablesPath = organizationId ? `organizations/${organizationId}/receivables` : null;
  const ordersPath = organizationId ? `organizations/${organizationId}/orders` : null;
  const isSalespersonView = userRole === 'Salesperson';

  useEffect(() => {
    const currentUserId = auth.currentUser?.uid;

    if (!organizationId || listMode === 'none' || (isSalespersonView && !currentUserId)) {
      setReceivables([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const colRef = collection(db, `organizations/${organizationId}/receivables`);

    const applyDocs = (docs) => {
      let items = docs.map(mapDoc);
      if (isSalespersonView) {
        items = items.filter((item) => item.salespersonId === currentUserId);
      }
      if (listMode === 'completed') {
        items = items
          .filter((item) => item.status === RECEIVABLE_STATUS.PAID)
          .sort((a, b) => (b.paidAt?.toMillis?.() || 0) - (a.paidAt?.toMillis?.() || 0));
      } else {
        items = items
          .filter((item) => OPEN_STATUSES.includes(item.status))
          .sort((a, b) => (a.dueDate?.toMillis?.() || 0) - (b.dueDate?.toMillis?.() || 0));
      }
      setReceivables(items);
      setError(null);
      setLoading(false);
    };

    const fallbackQuery = isSalespersonView
      ? query(colRef, where('salespersonId', '==', currentUserId))
      : query(colRef);

    const constraints = [];
    if (isSalespersonView) {
      constraints.push(where('salespersonId', '==', currentUserId));
    }
    if (listMode === 'completed') {
      constraints.push(where('status', '==', RECEIVABLE_STATUS.PAID), orderBy('paidAt', 'desc'));
    } else {
      constraints.push(where('status', 'in', OPEN_STATUSES), orderBy('dueDate', 'asc'));
    }

    const onError = (snapshotError) => {
      setError(isPermissionError(snapshotError)
        ? RECEIVABLE_ERRORS.PERMISSION_DENIED
        : snapshotError.message);
      setLoading(false);
    };

    let unsubscribe = onSnapshot(
      query(colRef, ...constraints),
      (snapshot) => applyDocs(snapshot.docs),
      (snapshotError) => {
        if (!isIndexError(snapshotError)) {
          onError(snapshotError);
          return;
        }
        unsubscribe();
        unsubscribe = onSnapshot(
          fallbackQuery,
          (snapshot) => applyDocs(snapshot.docs),
          onError
        );
      }
    );

    return () => unsubscribe();
  }, [organizationId, isSalespersonView, listMode]);

  const getLinkedOrderIds = useCallback(async () => {
    const linked = new Set();
    if (!receivablesPath) return linked;

    try {
      const snapshot = await getDocs(collection(db, receivablesPath));
      snapshot.docs.forEach((item) => {
        const data = item.data();
        if (data.source === RECEIVABLE_SOURCE.ORDER) {
          linked.add(item.id);
        }
        if (data.orderId) {
          linked.add(data.orderId);
        }
      });
    } catch (linkedError) {
      if (!isPermissionError(linkedError)) {
        throw wrapFirestoreError(linkedError);
      }
    }
    return linked;
  }, [receivablesPath]);

  const loadEligibleOrders = useCallback(
    async () => {
      if (!ordersPath) return [];

      const ordersSnapshot = await getDocs(collection(db, ordersPath));
      const eligible = ordersSnapshot.docs
        .map(mapDoc)
        .filter((order) => ELIGIBLE_ORDER_STATUSES.includes(order.status))
        .sort((a, b) => Number(b.orderNumber || 0) - Number(a.orderNumber || 0));

      let linkedIds = new Set();
      try {
        linkedIds = await getLinkedOrderIds();
      } catch (linkedError) {
        // Still show eligible orders if the receivables read fails.
      }

      return eligible.filter((order) => !linkedIds.has(order.id));
    },
    [ordersPath, getLinkedOrderIds]
  );

  const findOrderByNumber = useCallback(
    async (rawNumber) => {
      if (!ordersPath) {
        throw new Error(RECEIVABLE_ERRORS.ORDER_NOT_FOUND);
      }

      const term = normalizeSearchTerm(rawNumber);
      const numeric = Number(term);
      if (!term) {
        throw new Error(RECEIVABLE_ERRORS.ORDER_NOT_FOUND);
      }

      let order = null;

      try {
        if (numeric && !isNaN(numeric)) {
          const numericSnapshot = await getDocs(
            query(
              collection(db, ordersPath),
              where('orderNumber', '==', numeric),
              limit(1)
            )
          );
          if (!numericSnapshot.empty) {
            order = mapDoc(numericSnapshot.docs[0]);
          }
        }

        if (!order) {
          const stringSnapshot = await getDocs(
            query(
              collection(db, ordersPath),
              where('orderNumber', '==', term),
              limit(1)
            )
          );
          if (!stringSnapshot.empty) {
            order = mapDoc(stringSnapshot.docs[0]);
          }
        }

        if (!order) {
          const matches = await loadEligibleOrders();
          const exact = matches.find((item) => String(item.orderNumber) === term || Number(item.orderNumber) === numeric);
          order = exact || null;
        }
      } catch (lookupError) {
        throw wrapFirestoreError(lookupError);
      }

      if (!order) {
        throw new Error(RECEIVABLE_ERRORS.ORDER_NOT_FOUND);
      }
      if (!ELIGIBLE_ORDER_STATUSES.includes(order.status)) {
        throw new Error(RECEIVABLE_ERRORS.ORDER_NOT_ELIGIBLE);
      }

      const linkedIds = await getLinkedOrderIds();
      if (linkedIds.has(order.id)) {
        throw new Error(RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED);
      }

      if (receivablesPath) {
        try {
          const existing = await getDoc(doc(db, `${receivablesPath}/${order.id}`));
          if (existing.exists()) {
            throw new Error(RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED);
          }
        } catch (existingError) {
          if (existingError.message === RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED) {
            throw existingError;
          }
          if (!isPermissionError(existingError)) {
            throw wrapFirestoreError(existingError);
          }
        }
      }

      return order;
    },
    [ordersPath, receivablesPath, getLinkedOrderIds, loadEligibleOrders]
  );

  const getReceivable = useCallback(
    async (receivableId) => {
      if (!receivablesPath || !receivableId) return null;
      try {
        const snapshot = await getDoc(doc(db, `${receivablesPath}/${receivableId}`));
        if (!snapshot.exists()) return null;
        return mapDoc(snapshot);
      } catch (readError) {
        throw wrapFirestoreError(readError);
      }
    },
    [receivablesPath]
  );

  const createSale = useCallback(
    async ({
      customerName,
      invoiceAmount,
      saleDate,
      creditDays,
      salespersonId,
      orderId = null,
      orderNumber = null,
      note = ''
    }) => {
      if (!receivablesPath) throw new Error('Organization not available');

      const amount = roundMoney(invoiceAmount);
      const saleDay = startOfDay(saleDate);
      const days = Number(creditDays);
      const dueDay = addDaysSafe(saleDay, days);

      const payload = stripUndefined({
        customerName,
        source: orderId ? RECEIVABLE_SOURCE.ORDER : RECEIVABLE_SOURCE.MANUAL,
        orderId: orderId || null,
        orderNumber: orderNumber ?? null,
        salespersonId: salespersonId || null,
        invoiceAmount: amount,
        amountReceived: 0,
        amountOutstanding: amount,
        status: RECEIVABLE_STATUS.UNPAID,
        saleDate: toTimestamp(saleDay),
        creditDays: days,
        dueDate: toTimestamp(dueDay),
        remindersSent: emptyReminders(),
        note: note || '',
        paidAt: null,
        createdBy: auth.currentUser?.uid || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      try {
        if (orderId) {
          const receivableRef = doc(db, `${receivablesPath}/${orderId}`);
          await runTransaction(db, async (transaction) => {
            const existing = await transaction.get(receivableRef);
            if (existing.exists()) {
              throw new Error(RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED);
            }
            transaction.set(receivableRef, payload);
          });
          return orderId;
        }

        const created = await addDoc(collection(db, receivablesPath), payload);
        return created.id;
      } catch (writeError) {
        if (writeError.message === RECEIVABLE_ERRORS.ORDER_ALREADY_LINKED) {
          throw writeError;
        }
        throw wrapFirestoreError(writeError);
      }
    },
    [receivablesPath]
  );

  const updateSale = useCallback(
    async (receivableId, {
      customerName,
      invoiceAmount,
      saleDate,
      creditDays,
      salespersonId,
      note = ''
    }) => {
      if (!receivablesPath || !receivableId) throw new Error('Organization not available');

      const receivableRef = doc(db, `${receivablesPath}/${receivableId}`);

      try {
        await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(receivableRef);
          if (!snapshot.exists()) {
            throw new Error('Sale not found');
          }

          const current = snapshot.data();
          const amount = roundMoney(invoiceAmount);
          const received = roundMoney(current.amountReceived);
          if (amount < received) {
            throw new Error(RECEIVABLE_ERRORS.AMOUNT_BELOW_RECEIVED);
          }

          const saleDay = startOfDay(saleDate);
          const days = Number(creditDays);
          const dueDay = addDaysSafe(saleDay, days);
          const previousDue = startOfDay(current.dueDate);
          const dueChanged = !previousDue || previousDue.getTime() !== dueDay?.getTime();
          const outstanding = roundMoney(amount - received);
          const status = computeStatus(amount, received);

          transaction.update(receivableRef, stripUndefined({
            customerName,
            invoiceAmount: amount,
            amountOutstanding: outstanding,
            status,
            saleDate: toTimestamp(saleDay),
            creditDays: days,
            dueDate: toTimestamp(dueDay),
            salespersonId: salespersonId || null,
            note: note || '',
            remindersSent: dueChanged ? emptyReminders() : (current.remindersSent || emptyReminders()),
            paidAt: status === RECEIVABLE_STATUS.PAID ? (current.paidAt || serverTimestamp()) : null,
            updatedAt: serverTimestamp()
          }));
        });
      } catch (writeError) {
        if (writeError.message === RECEIVABLE_ERRORS.AMOUNT_BELOW_RECEIVED) {
          throw writeError;
        }
        throw wrapFirestoreError(writeError);
      }
    },
    [receivablesPath]
  );

  const recordPayment = useCallback(
    async (receivableId, amount) => {
      if (!receivablesPath || !receivableId) throw new Error('Organization not available');

      const paymentAmount = roundMoney(amount);
      const receivableRef = doc(db, `${receivablesPath}/${receivableId}`);

      try {
        await runTransaction(db, async (transaction) => {
          const snapshot = await transaction.get(receivableRef);
          if (!snapshot.exists()) {
            throw new Error('Sale not found');
          }

          const current = snapshot.data();
          const invoiceAmount = roundMoney(current.invoiceAmount);
          const received = roundMoney(roundMoney(current.amountReceived) + paymentAmount);

          if (received > invoiceAmount) {
            throw new Error(RECEIVABLE_ERRORS.AMOUNT_EXCEEDS_OUTSTANDING);
          }

          const outstanding = roundMoney(invoiceAmount - received);
          const status = computeStatus(invoiceAmount, received);

          transaction.update(receivableRef, {
            amountReceived: received,
            amountOutstanding: outstanding,
            status,
            paidAt: status === RECEIVABLE_STATUS.PAID ? serverTimestamp() : null,
            updatedAt: serverTimestamp()
          });
        });
      } catch (writeError) {
        if (writeError.message === RECEIVABLE_ERRORS.AMOUNT_EXCEEDS_OUTSTANDING) {
          throw writeError;
        }
        throw wrapFirestoreError(writeError);
      }
    },
    [receivablesPath]
  );

  const removeSale = useCallback(
    async (receivableId) => {
      if (!receivablesPath || !receivableId) throw new Error('Organization not available');
      try {
        await deleteDoc(doc(db, `${receivablesPath}/${receivableId}`));
      } catch (deleteError) {
        throw wrapFirestoreError(deleteError);
      }
    },
    [receivablesPath]
  );

  return {
    receivables,
    loading,
    error,
    findOrderByNumber,
    loadEligibleOrders,
    getReceivable,
    createSale,
    updateSale,
    recordPayment,
    removeSale
  };
};

const addDaysSafe = (saleDay, days) => {
  if (!saleDay) return null;
  const dueDay = new Date(saleDay);
  dueDay.setDate(dueDay.getDate() + Number(days || 0));
  return dueDay;
};

export default useReceivables;
